// AGC: ConsultantCard - Card component for displaying a consultant in the marketplace
// AGC: Shows photo, name, rating, specializations, and price range

import React from 'react'
import { 
  Star, 
  CheckCircle, 
  Clock,
  MessageCircle,
  ChevronRight,
} from 'lucide-react'
import { 
  type ConsultantProfile,
  SPECIALIZATION_INFO,
  LEVEL_INFO,
} from '../../types/advisor'

interface ConsultantCardProps {
  consultant: ConsultantProfile
  onViewProfile?: (consultantId: string) => void
  compact?: boolean
}

/**
 * AGC: Consultant Card Component
 */
export const ConsultantCard: React.FC<ConsultantCardProps> = ({
  consultant,
  onViewProfile,
  compact = false,
}) => {
  const levelInfo = LEVEL_INFO[consultant.level]

  // AGC: Get top specializations to display
  const topSpecializations = consultant.specializations?.slice(0, 3) || []

  if (compact) {
    return (
      <button
        onClick={() => onViewProfile?.(consultant.id)}
        className="w-full flex items-center gap-3 p-3 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 active:ring-4 active:ring-[var(--ring-click-primary)] transition-colors text-left"
      >
        {/* AGC: Avatar */}
        <div className="relative">
          {consultant.photoURL ? (
            <img
              src={consultant.photoURL}
              alt={consultant.displayName}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <span className="text-white font-bold text-lg">
                {consultant.displayName.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          {consultant.isVerified && (
            <CheckCircle className="absolute -bottom-0.5 -right-0.5 w-4 h-4 text-blue-500 bg-white dark:bg-slate-800 rounded-full" />
          )}
        </div>

        {/* AGC: Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
              {consultant.displayName}
            </span>
            <span className="text-sm">{levelInfo.badge}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
            <span>{consultant.rating.toFixed(1)}</span>
            <span>·</span>
            <span>{consultant.totalConsultations} consultas</span>
          </div>
        </div>

        <ChevronRight className="w-5 h-5 text-gray-400" />
      </button>
    )
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 overflow-hidden hover:shadow-lg transition-shadow">
      {/* AGC: Header with gradient */}
      <div className="relative h-20 bg-gradient-to-br from-emerald-500 to-teal-600">
        {/* AGC: Level badge */}
        <div className="absolute top-3 right-3 px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs font-medium flex items-center gap-1">
          <span>{levelInfo.badge}</span>
          <span>{levelInfo.name}</span>
        </div>
      </div>

      {/* AGC: Avatar */}
      <div className="relative -mt-10 px-4">
        <div className="relative inline-block">
          {consultant.photoURL ? (
            <img
              src={consultant.photoURL}
              alt={consultant.displayName}
              className="w-20 h-20 rounded-2xl object-cover border-4 border-white dark:border-slate-800"
            />
          ) : (
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 border-4 border-white dark:border-slate-800 flex items-center justify-center">
              <span className="text-white font-bold text-3xl">
                {consultant.displayName.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          {consultant.isVerified && (
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center border-2 border-white dark:border-slate-800">
              <CheckCircle className="w-4 h-4 text-white" />
            </div>
          )}
        </div>
      </div>

      {/* AGC: Content */}
      <div className="p-4 pt-3">
        {/* AGC: Name and rating */}
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100">
              {consultant.displayName}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {consultant.yearsExperience} anos de experiência
            </p>
          </div>
          <div className="flex items-center gap-1 px-2 py-1 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
            <span className="font-bold text-yellow-700 dark:text-yellow-400">
              {consultant.rating.toFixed(1)}
            </span>
          </div>
        </div>

        {/* AGC: Bio preview */}
        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">
          {consultant.bio}
        </p>

        {/* AGC: Specializations */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {topSpecializations.map(spec => {
            const specInfo = SPECIALIZATION_INFO[spec]
            return (
              <span
                key={spec}
                className="px-2 py-1 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 rounded-lg text-xs font-medium"
              >
                {specInfo?.icon} {specInfo?.name || spec}
              </span>
            )
          })}
          {consultant.specializations.length > 3 && (
            <span className="px-2 py-1 bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400 rounded-lg text-xs">
              +{consultant.specializations.length - 3}
            </span>
          )}
        </div>

        {/* AGC: Stats */}
        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-4">
          <div className="flex items-center gap-1">
            <MessageCircle className="w-4 h-4" />
            <span>{consultant.totalConsultations} consultas</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            <span>{consultant.responseTime}</span>
          </div>
        </div>

        {/* AGC: Price and CTA */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-slate-700">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">A partir de</p>
            <p className="font-bold text-lg text-gray-900 dark:text-gray-100">
              R$ {consultant.priceRange.min}
            </p>
          </div>
          <button
            onClick={() => onViewProfile?.(consultant.id)}
            className="px-4 py-2 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 active:ring-4 active:ring-[var(--ring-click-primary)] transition-colors"
          >
            Ver Perfil
          </button>
        </div>
      </div>
    </div>
  )
}

export default ConsultantCard





